import React, { useState, useContext, useEffect } from 'react'
import styled from '@emotion/styled'

import {validateEmail} from '../../helpers/validation-helpers'
import {registerEmail} from '../../api'
import analyticsTriggers from '../../analytics/triggers';

import { color, font, breakpoint } from '../../constants';

import { PageContext } from '../PocketHitsShared/page-context';
import EmailForm from './email-form'
import ArrowIcon from './arrow-icon'
import logoImage from './images/logo.svg'
import rainbowImage from './images/rainbow-reader.svg'

// publisher logos
import logoPopSci from './images/publishers/logo-popularScience.png'
import logoCitylab from './images/publishers/logo-cityLab.png'
import logoNautilus from './images/publishers/logo-nautilus.png'
import logoOutside from './images/publishers/logo-outside.png'
import logoAtlantic from './images/publishers/logo-theAtlantic.png'
import logoMentalFloss from './images/publishers/logo-mentalFloss.png'
import logoTexMonthly from './images/publishers/logo-texasMonthly.png'
import logoSaveur from './images/publishers/logo-saveur.png'
import logoNarratively from './images/publishers/logo-narratively.png'
import logoAtlasObscura from './images/publishers/logo-atlasObscura.png'
import logoHarvardBiz from './images/publishers/logo-harvardReview.png'
import logoKiplinger from './images/publishers/logo-kiplinger.png'
import logoFastCo from './images/publishers/logo-fastCompany.png'
import logoInc from './images/publishers/logo-inc.png'
import logoQuartz from './images/publishers/logo-quartz.png'
import logoGuardian from './images/publishers/logo-guardian.png'


const INVALID_EMAIL_ERROR = "Invalid email address";
const DEFAULT_ERROR = "Oops! Something went wrong.";


const WindowWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: hidden;
`

const Container = styled.div`
  padding: 0 1em;
  font-family: ${font.GRAPHIK};
  color: ${color.gray80};
  font-size: 16px;
  font-weight: 300;

  max-width: 660px;
  margin: 0 auto 4em auto;

  p, span, a {
    font-size: 0.875em;
  }

  a {
    font-weight: 400;
  }



  ${breakpoint.atSmallWidth} {
    .inset {
      padding: 0 0.4em;
    }
  }

  ::placeholder {
    color: ${color.gray80};
  }
`

const Logo = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  margin: 3em 0 2em;

  a {
    display: block;
    overflow: hidden;
    width: 155px;
    height: 41px;
    text-indent: -100%;
    background-repeat: no-repeat;
    background-image: url(${logoImage});
    background-size: contain;
    background-position: center;

    ${breakpoint.atSmallWidth} {
      width: 100px;
      height: 26px;
    }
  }

  ${breakpoint.atMediumWidth} {
    margin: 2em 0 1em;
  }

  ${breakpoint.atSmallWidth} {
    margin: 2.5em 0 1.5em;
  }
`

const Heading1 = styled.h1`
  font-family: ${font.DOYLE};
  font-size: 2.1em;
  line-height: 1.333em;
  font-weight: normal;
  text-align: center;
  color: #000;
  margin: 0 0 0.5em;

  ${breakpoint.atSmallWidth} {
    font-size: 1.5em;
    margin: 0 0 0.75em;
  }
`

const Heading3 = styled.h3`
  font-size: 1.125em;
  line-height: 1.375em;
  font-weight: 300;
  text-align: center;
  margin: 0 0 2em;

  ${breakpoint.atMediumWidth} {
    font-size: 1em;
  }

  ${breakpoint.atSmallWidth} {
    text-align: left;
  }
`

const Heading4 = styled.h4`
  font-size: 1em;
  font-weight: normal;
  padding: 0 0 0.5em 0;
  margin: 0 0 2em;
  border-bottom: 2px solid;

  &.border-amber {
    border-color: ${color.amber100};
  }

  &.border-coral {
    border-color: ${color.coral80};
  }
`

const Link = styled.a`
  color: ${color.coral80};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const FormContainer = styled.div`
  margin: 0 auto 3em;

  ${breakpoint.atSmallWidth} {
    margin-bottom: 2em;
  }
`

const ExampleLink = styled.div`
  margin: 1em 0 0;
`

const Hero = styled.img`
  position: relative;
  /* graphic is styled to overflow the boundaries of the content, so that on larger
  screens the whole thing is visible, at small screens it becomes cropped. */
  top: -6%;
  left: -6%;
  width: 112%;
  margin: 0 0 3em;

  ${breakpoint.atSmallWidth} {
    width: 140%;
    top: -20%;
    left: -20%;
    margin: 0 0 2em;
  }
`

const Publishers = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 110px);
  grid-row-gap: 2em;
  justify-content: space-between;
  justify-items: center;
  margin: 0 0 2em 0;

  img {
    max-width: 110px;
  }

  @media (max-width: 580px) {
    grid-template-columns: repeat(3, 110px);
  }

  @media (max-width: 440px) {
    grid-template-columns: repeat(2, 110px);
    justify-content: space-around;
  }
`

export default () => {
  const {csrfSymfony} = useContext(PageContext);
  const [isSubmitting, setSubmitting] = useState(false); 
  const [isSubmitSuccessful, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // we have two instances of the same form on the same page, so in order to prevent
  // error states from appearing on both forms at the same time, we'll
  // use an id to only apply error state to the form that did the submission.
  const [formId, setFormId] = useState(null);

  // on component mount
  useEffect(() => {
    analyticsTriggers.pocketHitsStandaloneSignupPageView();
  }, []);

  function handleInputFocus(focusFormId) {
    analyticsTriggers.pocketHitsSignupFieldFocus(focusFormId);
  }
  
  function handleFormSubmit(email, submitFormId) {
    setFormId(submitFormId);

    analyticsTriggers.pocketHitsSignupSubmit(submitFormId);

    if (!validateEmail(email)) {
      analyticsTriggers.pocketHitsSignupSubmitError(submitFormId)
      setSubmitError(INVALID_EMAIL_ERROR);

    } else {
      const csrfToken = csrfSymfony && csrfSymfony.token;
      setSubmitError(null);
      setSubmitting(true);

      registerEmail(
        { email, xt: csrfToken, utm_source: 'marketing', utm_campaign: 'explore-signup' },
        handleSubmitSuccess,
        handleSubmitError,
        handleSubmitFinished
      )
    }
  }

  function handleFieldChange() {
    // clear any errors if the user changes the field value
    setSubmitError(null);
  }

  function handleSubmitFinished() {
    setSubmitting(false);
  }

  /**
   * Called when the request to submit the email has succeeded
   * @param   {Object}  refreshedSignedParams  updated params for API calls
   */
  function handleSubmitSuccess(refreshedSignedParams) {
    analyticsTriggers.pocketHitsSignupSubmitSuccess(formId);
    setSubmitSuccess(true);
  }

  /**
   * Called when the request to submit the email failed
   */
  function handleSubmitError() {
    analyticsTriggers.pocketHitsSignupSubmitError(formId)
    setSubmitError(DEFAULT_ERROR);
  }

  return (
    <WindowWrapper>
      <Container>
        <Logo>
          <a href="/">Pocket</a>
        </Logo>

        {!isSubmitSuccessful &&
          <React.Fragment>
            <Heading1>Your guide to the most fascinating articles from top&nbsp;publications.</Heading1>
            <Heading3 className="inset">Join millions of Pocket Hits subscribers and get inspired&nbsp;daily.</Heading3>
            <FormContainer>
              <EmailForm 
                id="top"
                onFocus={handleInputFocus}
                onChange={handleFieldChange}
                onSubmit={handleFormSubmit}
                isSubmitting={formId === 'top' && isSubmitting}
                submitError={formId === 'top' ? submitError : null}
              />
              <ExampleLink>
                <span className="inset">Not sure yet?</span>
                {' '}
                <Link href="https://getpocket.com/explore/pocket-hits" target="_blank" rel="noopener noreferrer">See an example&nbsp;<ArrowIcon /></Link>
              </ExampleLink>
            </FormContainer>
          </React.Fragment>
        }

        {isSubmitSuccessful &&
          <React.Fragment>
            <Heading1>Please check your inbox</Heading1>
            <Heading3>We’ve sent you an email to confirm your subscription. You’re
            one step away from a daily dose of the best stories from around&nbsp;the&nbsp;web.</Heading3>
          </React.Fragment>
        }

        <Hero src={rainbowImage} />

        <Heading4 className="border-amber">See stories from publishers like these</Heading4>

        <Publishers>
          <img src={logoPopSci} alt="Popular Science" />
          <img src={logoCitylab} alt="Citylab" />
          <img src={logoNautilus} alt="Nautilus" />
          <img src={logoOutside} alt="Outside" />
          <img src={logoAtlantic} alt="The Atlantic" />
          <img src={logoMentalFloss} alt="Mental Floss" />
          <img src={logoTexMonthly} alt="Texas Monthly" />
          <img src={logoSaveur} alt="Saveur" />
          <img src={logoNarratively} alt="Narratively" />
          <img src={logoAtlasObscura} alt="Atlas Obscura" />
          <img src={logoHarvardBiz} alt="Harvard Business Review" />
          <img src={logoKiplinger} alt="Kiplinger" />
          <img src={logoFastCo} alt="Fast Company" />
          <img src={logoInc} alt="Inc." />
          <img src={logoQuartz} alt="Quartz" />
          <img src={logoGuardian} alt="The Guardian" />
        </Publishers>

        {!isSubmitSuccessful &&
          <React.Fragment>
            <Heading4 className="border-coral">Sign up for Pocket Hits</Heading4>
            <EmailForm
              id="bottom"
              isSubmitting={formId === 'bottom' && isSubmitting}
              onFocus={handleInputFocus}
              onChange={handleFieldChange}
              onSubmit={handleFormSubmit}
              submitError={formId === 'bottom' ? submitError : null}
            />
          </React.Fragment>
        }

      </Container>
    </WindowWrapper>
  );
}
